import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { deliveryAgentAPI } from './deliveryAgentAPI';

const EditDeliveryAgent = ({ open, onClose, onSuccess, agent }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employeeId: '',
    agentRole: 'delivery',
    vehicleType: 'bike',
    vehicleNumber: '',
    licenseNumber: '',
    panNumber: '',
    aadhaarNumber: '',
    maxOrderCapacity: 5,
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    }
  });
  const [errors, setErrors] = useState({});

  // Populate form when agent data changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        email: agent.email || '',
        phone: agent.phone || '',
        employeeId: agent.employeeId || '',
        agentRole: agent.agentRole || 'delivery',
        vehicleType: agent.vehicleType || 'bike',
        vehicleNumber: agent.vehicleNumber || '',
        licenseNumber: agent.licenseNumber || '',
        panNumber: agent.panNumber || '',
        aadhaarNumber: agent.aadhaarNumber || '',
        maxOrderCapacity: agent.maxOrderCapacity || 5,
        address: {
          street: agent.address?.street || '',
          city: agent.address?.city || '',
          state: agent.address?.state || '',
          pincode: agent.address?.pincode || ''
        }
      });
    }
  }, [agent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "panNumber") {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    }
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.employeeId.trim()) newErrors.employeeId = 'Employee ID is required';
    if (!formData.panNumber.trim()) newErrors.panNumber = 'PAN number is required';
    if (!formData.aadhaarNumber.trim()) newErrors.aadhaarNumber = 'Aadhaar number is required';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
     // ✅ PAN validation (FIXED)
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.panNumber.toUpperCase())) {
      newErrors.panNumber = 'Invalid PAN format (e.g. ABCDE1234F)';
    }

    if (!/^\d{12}$/.test(formData.aadhaarNumber))
      newErrors.aadhaarNumber = 'Aadhaar must be exactly 12 digits';

    const phoneRegex = /^[0-9]{10}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await deliveryAgentAPI.update(agent.agentId, formData);
      if (response.data.success) {
        onSuccess(response.data.message);
        onClose();
      }
    } catch (error) {
      onSuccess(error.response?.data?.message || 'Error updating agent', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#006D77', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Delivery Agent
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Employee ID"
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              error={!!errors.employeeId}
              helperText={errors.employeeId}
              required
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Agent Role"
              name="agentRole"
              value={formData.agentRole}
              onChange={handleChange}
              required
            >
              <MenuItem value="delivery">Delivery Agent</MenuItem>
              <MenuItem value="sample_collection">Sample Collection</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Vehicle Type"
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              required
            >
              <MenuItem value="bike">Bike</MenuItem>
              <MenuItem value="car">Car</MenuItem>
              <MenuItem value="van">Van</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Vehicle Number"
              name="vehicleNumber"
              value={formData.vehicleNumber}
              onChange={handleChange}
              error={!!errors.vehicleNumber}
              helperText={errors.vehicleNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Number"
              name="licenseNumber"
              value={formData.licenseNumber}
              onChange={handleChange}
              error={!!errors.licenseNumber}
              helperText={errors.licenseNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN Number"
              name="panNumber"
              value={formData.panNumber.toUpperCase()}
              onChange={handleChange}
              error={!!errors.panNumber}
              helperText={errors.panNumber}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Aadhaar Number"
              name="aadhaarNumber"
              value={formData.aadhaarNumber}
              onChange={handleChange}
              error={!!errors.aadhaarNumber}
              helperText={errors.aadhaarNumber}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Max Order Capacity"
              name="maxOrderCapacity"
              type="number"
              value={formData.maxOrderCapacity}
              onChange={handleChange}
              inputProps={{ min: 1 }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="State"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Pincode"
              name="address.pincode"
              value={formData.address.pincode}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005c64' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Update Agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeliveryAgent;